// План продаж из Google-таблицы, как у чек-листов (Алексей 28.10, #3191). Ссылка → сразу синк, дальше
// раз в час сам. Формат — как сетка «План на месяц»: A — позиция, «Обычно», дни (01.11 или 1). Синк
// заменяет только цели по дням; «Предлагаем» и напоминания остаются из настроек. BE: sales_plan_sheets.py.
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { extractApiError } from '@/utils/apiError';
import {
  createSalesPlanSheet, deleteSalesPlanSheet, getSalesPlanSheet, putSalesPlanSheet, syncSalesPlanSheet, type SalesPlanSheetDto,
} from '@/api/salesPlan';

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export function SalesPlanSheetSync({ zoneId, disabled, onSynced }: { zoneId: string; disabled: boolean; onSynced: () => void }) {
  const [inFlight, setInFlight] = useState(false);
  const { data, mutate } = useSWR(['sales-plan-sheet', zoneId], () => getSalesPlanSheet(zoneId).then((r) => r.data.sheet),
    { refreshInterval: inFlight ? 2000 : 0 });
  const [url, setUrl] = useState('');
  const [ws, setWs] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sheet: SalesPlanSheetDto | null | undefined = data;

  useEffect(() => {
    if (sheet) { setUrl(sheet.sheet_url); setWs(sheet.worksheet_title); }
  }, [sheet?.sheet_url, sheet?.worksheet_title]);

  const running = sheet?.sync_state === 'pending' || sheet?.sync_state === 'running';
  useEffect(() => {
    if (inFlight && sheet && !running) {
      setInFlight(false);
      if (sheet.sync_state === 'ok') onSynced();
    }
  }, [inFlight, running, sheet, onSynced]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await mutate();
      setInFlight(true);
    } catch (e) {
      setError(extractApiError(e, 'Не получилось'));
    } finally {
      setBusy(false);
    }
  }

  const changed = !sheet || url.trim() !== sheet.sheet_url || ws.trim() !== sheet.worksheet_title;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input className="h-9 min-w-[260px] flex-1" placeholder="https://docs.google.com/spreadsheets/…" value={url}
          disabled={disabled || busy} onChange={(e) => setUrl(e.target.value)} />
        <Input className="h-9 w-40" placeholder="Лист (первый)" value={ws} disabled={disabled || busy} onChange={(e) => setWs(e.target.value)} />
        {changed ? (
          <Button size="sm" disabled={disabled || busy || !url.trim()} onClick={() => run(() => putSalesPlanSheet(zoneId, url.trim(), ws.trim()))}>
            {busy && <Loader2 className="size-3.5 animate-spin" />} Подключить
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled={disabled || busy || running} onClick={() => run(() => syncSalesPlanSheet(zoneId))}>
            <RefreshCw className={running ? 'size-3.5 animate-spin' : 'size-3.5'} /> Синхронизировать
          </Button>
        )}
        {!sheet && !url.trim() && (
          <Button size="sm" variant="outline" disabled={disabled || busy} onClick={() => run(() => createSalesPlanSheet(zoneId))}>
            Создать таблицу
          </Button>
        )}
        {sheet && (
          <a href={sheet.sheet_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
            Открыть <ExternalLink className="size-3" />
          </a>
        )}
        {sheet && !disabled && (
          <button type="button" className="text-xs text-muted-foreground hover:text-destructive" onClick={() => run(async () => { await deleteSalesPlanSheet(zoneId); setUrl(''); setWs(''); })}>
            Отвязать
          </button>
        )}
      </div>
      <div className="text-xs text-muted-foreground">
        {running ? 'Читаем таблицу…'
          : sheet?.sync_state === 'ok' ? `Синхронизировано ${when(sheet.sync_finished_at)}: целей ${sheet.sync_items_count ?? 0}. Дальше — раз в час сам.`
          : sheet?.sync_state === 'error' ? <span className="text-destructive">Ошибка {when(sheet.sync_finished_at)}: {sheet.last_sync_error}</span>
          : 'Вставьте ссылку (доступ по ссылке — читатель) или «Создать таблицу»: шапка месяца и текущие цели, доступ по ссылке на редактирование. Формат: колонка A — позиция, «Обычно» — каждый день, дальше дни: 01.11 или 1 (месяц из названия листа «Ноябрь 2026»). Синк заменяет цели по дням; «Предлагаем» и напоминания остаются здесь.'}
      </div>
      {error && <div className="text-xs text-destructive">{error}</div>}
    </div>
  );
}
